'use client';

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useUIStore } from '~/store/useUIStore';
import { useToast } from '~/lib/toasts';

/**
 * Handler component that processes URL parameters to trigger UI actions
 * Currently supports:
 * - propertySearchOpen=true - Opens the property search flyout
 * - error & error_description - Shows error toast messages
 */
export const UrlParamsHandler = () => {
  const searchParams = useSearchParams();
  const processedParamsRef = useRef<Set<string>>(new Set());
  const { showToast } = useToast();

  useEffect(() => {
    if (!searchParams) {
      console.log('[UrlParamsHandler] No searchParams available');
      return;
    }

    // Extract all relevant params once
    const showPropertySearch = searchParams.get('showPropertySearch');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle property search modal
    if (
      showPropertySearch === 'true' &&
      !processedParamsRef.current.has('propertySearch')
    ) {
      processedParamsRef.current.add('propertySearch');

      const { modals, setModals } = useUIStore.getState();
      if (!modals.propertySearchOpen) {
        setModals({ ...modals, propertySearchOpen: true });
      }
    } else if (
      showPropertySearch !== 'true' &&
      processedParamsRef.current.has('propertySearch')
    ) {
      processedParamsRef.current.delete('propertySearch');
    }

    // Handle error messages
    if (error && !processedParamsRef.current.has('error')) {
      processedParamsRef.current.add('error');

      console.log('[UrlParamsHandler] Showing error toast:', {
        error,
        errorDescription,
      });

      const toastResult = showToast(
        {
          title: error,
          description: errorDescription || '',
        },
        { status: 'error', duration: 8000 }
      );

      console.log('[UrlParamsHandler] Toast result:', toastResult);

      // Keep the error params in URL - don't clean them up
      // This allows the error to be visible and debuggable
    } else if (!error && processedParamsRef.current.has('error')) {
      processedParamsRef.current.delete('error');
    }
  }, [searchParams]);

  return null; // This is a handler component, it doesn't render anything
};
